import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, TrendingUp, BookOpen, Globe, Award, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Rigorous safeguarding, risk management, and clinical governance underpin everything we do.'
    },
    {
      icon: Heart,
      title: 'Dignity & Respect',
      description: 'Every individual deserves to be treated with compassion, understanding, and person-centered care.'
    },
    {
      icon: TrendingUp,
      title: 'Recovery-Focused',
      description: 'We believe in empowering individuals to regain confidence, independence, and meaningful lives.'
    },
    {
      icon: BookOpen,
      title: 'Evidence-Based Practice',
      description: 'Our approach is grounded in clinical expertise, academic insight, and continuous learning.'
    },
    {
      icon: Globe,
      title: 'Cultural Competence',
      description: 'We respect diversity and provide culturally sensitive, inclusive care for all individuals.'
    },
    {
      icon: Award,
      title: 'Professional Excellence',
      description: 'RMN-led leadership, ongoing supervision, training, and reflective practice ensure high standards.'
    }
  ];

  const commitments = [
    'Safeguarding and risk management',
    'Staff training, supervision, and reflective practice',
    'Clear governance and accountability',
    'Learning from incidents and continuously improving care',
    'Working collaboratively with families, commissioners, and professionals',
    'Operating in line with regulatory expectations and best practice'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="heading-hero">About Three Spheres Healthcare</h1>
            <p className="body-large">
              A UK-based, mental-health-led care organisation dedicated to supporting adults with enduring mental health conditions.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-2 mb-6">Who We Are</h2>
              <div className="space-y-4 text-gray-700">
                <p className="body-medium">
                  Three Spheres Healthcare is a UK-based, mental-health-led care organisation dedicated to supporting adults with enduring mental health conditions and associated disabilities to live safe, meaningful, and fulfilling lives within the community.
                </p>
                <p className="body-medium">
                  Founded and led by a Registered Mental Health Nurse (RMN), Three Spheres Healthcare brings together frontline clinical expertise, academic insight, and strong governance principles to deliver high-quality, person-centred supported living services.
                </p>
                <p className="body-medium">
                  Our approach is grounded in compassion, dignity, safety, and evidence-based practice, ensuring that individuals are supported not just to exist, but to thrive.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1666887360680-9dc27a1d2753?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3MDM2NjMzNHww&ixlib=rb-4.1.0&q=85"
                alt="Healthcare professional"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Leadership - Founder */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 mb-12 text-center">Our Leadership</h2>
          <div className="max-w-5xl mx-auto">
            <Card className="p-8 md:p-12 border-0 overflow-hidden">
              <div className="grid lg:grid-cols-5 gap-8 items-start">
                <div className="lg:col-span-2">
                  <img 
                    src="/founder.jpg" 
                    alt="Prince Ezekwesirim Utchay - Founder & Director"
                    className="rounded-2xl w-full h-auto object-cover shadow-lg"
                  />
                </div>
                <div className="lg:col-span-3">
                  <div className="mb-6">
                    <h3 className="heading-2 mb-2">Prince Ezekwesirim Utchay</h3>
                    <p className="text-[#4A90E2] font-semibold text-lg">Founder & Director</p>
                    <p className="text-gray-600 text-sm mt-1">RMN | MBA | PhD Candidate (Mental Health)</p>
                  </div>
                  
                  <div className="space-y-4 text-gray-700">
                    <p className="body-medium">
                      Prince Ezekwesirim Utchay is the Founder and Director of Three Spheres Healthcare, bringing a strong blend of clinical expertise, academic depth, and leadership experience to the organisation.
                    </p>
                    <p className="body-medium">
                      He is a <strong>Registered Mental Health Nurse (RMN)</strong> with extensive experience working across NHS mental health services, acute and community settings, and agency environments, supporting adults with complex mental health needs. His clinical practice is grounded in person-centred, evidence-based care, with a strong emphasis on safety, dignity, and recovery.
                    </p>
                    <p className="body-medium">
                      Prince holds advanced academic qualifications in mental health and healthcare management, including an ongoing <strong>PhD in Mental Health</strong>, with research interests focused on patient safety, reduction of restrictive practices, and quality improvement in mental health services. He also holds an <strong>MBA in Healthcare Management</strong>, providing a strong foundation in governance, leadership, and service development.
                    </p>
                    
                    <div className="bg-[#E8F4FC] rounded-lg p-6 mt-6">
                      <h4 className="font-semibold text-lg mb-4 text-gray-900">Areas of Expertise</h4>
                      <ul className="grid md:grid-cols-2 gap-3">
                        {[
                          'Safeguarding Adults',
                          'Risk Assessment & Management',
                          'Clinical Governance',
                          'Quality Assurance',
                          'Leadership & Supervision',
                          'Equality & Diversity',
                          'Evidence-Based Practice',
                          'Service Development'
                        ].map((item, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                            <Award className="text-[#4A90E2] flex-shrink-0" size={16} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <p className="body-medium mt-6">
                      Three Spheres Healthcare is <strong>RMN-led</strong>, with clinical oversight embedded into service design, staff supervision, and quality improvement processes. Prince's vision is to deliver safe, compassionate, and well-governed services that meet Care Quality Commission (CQC) standards and respond effectively to the needs of individuals, families, and commissioning partners.
                    </p>

                    <p className="body-medium mt-4 text-gray-600 italic">
                      Clinical leadership arrangements will continue to develop in line with service growth and regulatory requirements.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Our Philosophy</h2>
            <Card className="p-8 md:p-12 border-0 bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7]">
              <blockquote className="text-2xl md:text-3xl font-semibold text-center mb-6 text-[#2C5F8D]">
                "Health and wellbeing are sustained through balance, structure, and connection."
              </blockquote>
              <p className="body-medium text-center text-gray-700 mb-8">
                Our philosophy recognises that individuals with long-term mental health needs require more than basic care — they need stability, understanding, meaningful relationships, and opportunities for growth.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4 text-gray-900">We Focus On:</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Whole-person, recovery-oriented care</li>
                    <li>• Promoting independence with appropriate support</li>
                    <li>• Creating safe environments that reduce crisis</li>
                    <li>• Empowering confidence, routine, and purpose</li>
                  </ul>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4 text-gray-900">Our Approach:</h3>
                  <p className="text-sm text-gray-700">
                    Services shaped by real-world clinical experience, reflective practice, and continuous learning, ensuring care is ethical, proportionate, and responsive to individual needs.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Motto */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Our Motto</h2>
            <p className="text-3xl md:text-4xl font-bold text-white">
              "The Spheres That Support Life"
            </p>
            <p className="body-large text-white/90">
              This motto reflects our commitment to creating the right conditions for people to live well — physically, psychologically, and socially — through balanced, compassionate, and structured support.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Values</h2>
            <p className="body-large max-w-2xl mx-auto">
              The principles that guide everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="heading-3 mb-3">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment to Quality */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Our Commitment to Quality & Safety</h2>
            <p className="body-medium text-center text-gray-700 mb-8">
              As a mental-health-led organisation, Three Spheres Healthcare places strong emphasis on:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {commitments.map((commitment, index) => (
                <div key={index} className="flex items-start gap-3 bg-white rounded-lg p-4 shadow-sm">
                  <Users className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-800">{commitment}</span>
                </div>
              ))}
            </div>
            <p className="body-medium text-center text-gray-700 mt-8">
              We are committed to operating in line with regulatory expectations and best practice standards, ensuring that the care we deliver is safe, effective, compassionate, and well-led.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2">Work With Us</h2>
            <p className="body-large text-gray-600">
              Whether you're looking to make a referral, explore partnership opportunities, or join our team, we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-[#4A90E2] to-[#7B68BE]">
                  Get in Touch
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white"
                >
                  View Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
